import { config } from '../config';

export interface IEyeAbleConfig {
    apiKey: string;
    position?: 'left' | 'right';
    topPosition?: string;
    iconSize?: number;
    language?: string;
}

/**
 * Eye-Able plugin configuration interface
 * Contains only the parameters actually used in this implementation
 */
interface IEyeAblePluginConfig {
    apiKey?: string;
    sidePosition?: 'left' | 'right';
    topPosition?: string;
    rightPosition?: string;
    mobileBottomPosition?: string;
    mobileRightPosition?: string;
    iconSize?: number;
    mobileIconSize?: number;
    mainIconAlt?: 0 | 1 | 2;
}

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        eyeAble_pluginConfig?: IEyeAblePluginConfig;
    }
}

/**
 * Eye-Able Accessibility Widget Service
 *
 * Provides integration with the Eye-Able accessibility widget.
 * Widget features are controlled by API key and managed mode in the Eye-Able dashboard.
 * Local configuration only supports positioning and appearance customization.
 */
export class EyeAbleService {
    private static readonly SCRIPT_URL =
        'https://www.eye-able-cdn.com/public/js/eyeAble.js';
    private static readonly DEFAULT_POSITION = 'right';
    private static readonly DEFAULT_TOP_POSITION = '10%';
    private static readonly DEFAULT_ICON_SIZE = 70;
    private static readonly DEFAULT_MOBILE_ICON_SIZE = 50;

    private initialized = false;

    /**
     * Loads and initializes the Eye-Able widget with configuration
     *
     * @throws {Error} If API key is missing
     */
    async load(): Promise<void> {
        const cfg = this.getConfig();

        if (!cfg) {
            throw new Error('[EyeAbleService] Configuration is missing');
        }

        if (!cfg.apiKey?.trim()) {
            throw new Error(
                '[EyeAbleService] API key is required but not provided',
            );
        }

        if (this.initialized) {
            console.warn(
                '[EyeAbleService] Service already initialized, skipping',
            );
            return;
        }

        this.setPluginConfiguration(cfg);
        await this.loadScript(EyeAbleService.SCRIPT_URL);

        this.initialized = true;
    }

    /**
     * Retrieves Eye-Able configuration from application config
     * Type assertion is required because config is imported from framework with dynamic structure
     */
    private getConfig(): IEyeAbleConfig | undefined {
        return (config as any).eyeAble as IEyeAbleConfig | undefined;
    }

    /**
     * Sets Eye-Able plugin configuration on window object
     * Only positioning and appearance can be configured locally
     */
    private setPluginConfiguration(cfg: IEyeAbleConfig): void {
        window.eyeAble_pluginConfig = {
            apiKey: cfg.apiKey,
            sidePosition: cfg.position || EyeAbleService.DEFAULT_POSITION,
            topPosition: cfg.topPosition || EyeAbleService.DEFAULT_TOP_POSITION,
            rightPosition: '10px',
            mobileBottomPosition: '15%',
            mobileRightPosition: '10px',
            iconSize: cfg.iconSize || EyeAbleService.DEFAULT_ICON_SIZE,
            mobileIconSize: EyeAbleService.DEFAULT_MOBILE_ICON_SIZE,
            mainIconAlt: 2,
        };
    }

    /**
     * Dynamically loads external script
     *
     * @param src - Script URL to load
     * @throws {Error} If script fails to load
     */
    private loadScript(src: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.async = true;
            script.src = src;

            script.onload = () => {
                resolve();
            };

            script.onerror = () => {
                const error = new Error(
                    `[EyeAbleService] Failed to load script: ${src}`,
                );
                console.error(error);
                reject(error);
            };

            document.head.appendChild(script);
        });
    }
}

export const eyeAbleService: EyeAbleService = new EyeAbleService();
